// src/pages/UsersPage.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Container,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tooltip,
  SelectChangeEvent,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  People as PeopleIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import { User, UserRole } from '../types/chat';
import { useRole } from '../hooks/useRole';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { countries } from '../shared/countries';

const UsersPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAdmin } = useRole();

  // Get country name based on current language
  const getCountryName = (countryCode: string) => {
    const country = countries.find(c => c.code === countryCode);
    if (!country) return countryCode;
    const lang = i18n.language.split('-')[0];
    switch (lang) {
      case 'tr': return country.nameTr || country.name;
      case 'de': return country.nameDe || country.name;
      case 'fr': return country.nameFr || country.name;
      case 'ru': return country.nameRu || country.name;
      case 'ar': return country.nameAr || country.name;
      default: return country.name;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0]; // YYYY-MM-DD format
    } catch {
      return dateString;
    }
  };
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [rooms, setRooms] = useState<{ id: string; roomNumber: string }[]>([]);
  const [roomManagementDialogOpen, setRoomManagementDialogOpen] = useState(false);
  const [selectedRooms, setSelectedRooms] = useState<string[]>([]);

  const fetchUsers = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/users');
      setUsers(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || t('users.failedToFetch'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchRooms = React.useCallback(async () => {
    try {
      const response = await api.get('/rooms');
      setRooms(response.data.map((room: any) => ({ id: room.id, roomNumber: room.roomNumber })));
    } catch (err: any) {
      console.error('Failed to fetch rooms:', err);
    }
  }, []);

  const filterUsers = React.useCallback(() => {
    let filtered = users;

    // Filter by role
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // Filter by search term (name or email)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.firstName.toLowerCase().includes(term) ||
        user.lastName.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term)
      );
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, roleFilter]);

  useEffect(() => {
    fetchUsers();
    fetchRooms();
  }, [fetchUsers, fetchRooms]);

  useEffect(() => {
    filterUsers();
  }, [filterUsers]);

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error">{t('users.noPermission')}</Alert>
      </Container>
    );
  }

  const handleViewDetails = (user: User) => {
    setSelectedUser(user);
    setDetailsDialogOpen(true);
  };

  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;

    try {
      await api.delete(`/users/${selectedUser.id}`);
      setUsers(users.filter(u => u.id !== selectedUser.id));
      setDeleteDialogOpen(false);
      setSelectedUser(null);
    } catch (err: any) {
      setError(err.response?.data?.message || t('users.failedToDelete'));
    }
  };

  const handleResetPasswordClick = (user: User) => {
    setSelectedUser(user);
    setNewPassword('');
    setResetPasswordDialogOpen(true);
  };

  const handleResetPasswordConfirm = async () => {
    if (!selectedUser || !newPassword) return;

    try {
      await api.put(`/users/${selectedUser.id}`, { password: newPassword });
      setResetPasswordDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
      alert(t('users.passwordResetSuccess'));
    } catch (err: any) {
      setError(err.response?.data?.message || t('users.failedToReset'));
    }
  };

  const handleManageRooms = (user: User) => {
    setSelectedUser(user);
    setSelectedRooms(user.rooms?.map(r => r.id) || []);
    setRoomManagementDialogOpen(true);
  };

  const handleSaveRoomAssignments = async () => {
    if (!selectedUser || selectedUser.role !== UserRole.OPERATOR) return;

    try {
      await api.put(`/users/${selectedUser.id}/assign-rooms`, { roomIds: selectedRooms });
      // Refresh users list
      await fetchUsers();
      setRoomManagementDialogOpen(false);
      setSelectedUser(null);
      setSelectedRooms([]);
      alert('Room assignments updated successfully');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update room assignments');
    }
  };

  const getRoleBadgeColor = (role: UserRole) => {
    switch (role) {
      case UserRole.SUPER_ADMIN:
        return 'secondary';
      case UserRole.ADMIN:
        return 'error';
      case UserRole.OPERATOR:
        return 'primary';
      case UserRole.GUEST:
        return 'default';
      default:
        return 'default';
    }
  };

  const getRoleIcon = (role: UserRole) => {
    switch (role) {
      case UserRole.SUPER_ADMIN:
        return '⭐';
      case UserRole.ADMIN:
        return '👑';
      case UserRole.OPERATOR:
        return '🎯';
      case UserRole.GUEST:
        return '👤';
      default:
        return '👤';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4, height: '100vh', overflow: 'auto' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Box>
            <Typography variant="h4" gutterBottom>
              {t('users.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('users.subtitle')}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/create-user')}
          startIcon={<PeopleIcon />}
        >
          {t('users.createNewUser')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            fullWidth
            placeholder={t('users.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>{t('users.filterByRole')}</InputLabel>
            <Select
              value={roleFilter}
              label={t('users.filterByRole')}
              onChange={(e: SelectChangeEvent<UserRole | 'all'>) => setRoleFilter(e.target.value as UserRole | 'all')}
              startAdornment={
                <InputAdornment position="start">
                  <FilterListIcon />
                </InputAdornment>
              }
            >
              <MenuItem value="all">{t('users.allRoles')}</MenuItem>
              <MenuItem value={UserRole.ADMIN}>👑 {t('sidebar.admin')}</MenuItem>
              <MenuItem value={UserRole.OPERATOR}>🎯 {t('sidebar.operator')}</MenuItem>
              <MenuItem value={UserRole.GUEST}>👤 {t('sidebar.guest')}</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          {t('users.showingUsers', { count: filteredUsers.length, total: users.length })}
        </Typography>
      </Paper>

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('users.name')}</TableCell>
              <TableCell>{t('users.email')}</TableCell>
              <TableCell>{t('users.role')}</TableCell>
              <TableCell>{t('users.roomId')}</TableCell>
              <TableCell>{t('users.reservationId')}</TableCell>
              <TableCell align="right">{t('users.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    {t('users.noUsersFound')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {user.firstName} {user.lastName}
                    </Typography>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={`${getRoleIcon(user.role)} ${user.role}`}
                      size="small"
                      color={getRoleBadgeColor(user.role)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {user.roomId || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {user.reservationId || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('users.viewDetails')}>
                      <IconButton size="small" onClick={() => handleViewDetails(user)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {user.role !== UserRole.ADMIN && user.role !== UserRole.SUPER_ADMIN && (
                      <Tooltip title={t('users.resetPassword')}>
                        <IconButton size="small" onClick={() => handleResetPasswordClick(user)}>
                          <LockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {user.role !== UserRole.ADMIN && user.role !== UserRole.SUPER_ADMIN && (
                      <Tooltip title={t('users.deleteUser')}>
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(user)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('users.userDetails')}</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('users.fullName')}</Typography>
                <Typography variant="body1">{selectedUser.firstName} {selectedUser.lastName}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('users.email')}</Typography>
                <Typography variant="body1">{selectedUser.email}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('users.role')}</Typography>
                <Chip
                  label={`${getRoleIcon(selectedUser.role)} ${selectedUser.role}`}
                  size="small"
                  color={getRoleBadgeColor(selectedUser.role)}
                  sx={{ mt: 0.5 }}
                />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('users.userId')}</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{selectedUser.id}</Typography>
              </Box>
              {/* Show rooms for operators */}
              {selectedUser.role === UserRole.OPERATOR && selectedUser.rooms && selectedUser.rooms.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Assigned Rooms</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                    {selectedUser.rooms.map((room) => (
                      <Chip key={room.id} label={`Room ${room.roomNumber}`} size="small" color="primary" variant="outlined" />
                    ))}
                  </Stack>
                  <Button 
                    size="small" 
                    onClick={() => handleManageRooms(selectedUser)}
                    sx={{ mt: 1 }}
                  >
                    Manage Rooms
                  </Button>
                </Box>
              )}
              {/* Show single room for guests */}
              {selectedUser.role === UserRole.GUEST && selectedUser.roomId && (
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('users.roomId')}</Typography>
                  <Typography variant="body2">{selectedUser.roomId}</Typography>
                </Box>
              )}
              {selectedUser.reservationId && (
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('users.reservationId')}</Typography>
                  <Typography variant="body2">{selectedUser.reservationId}</Typography>
                </Box>
              )}

              {/* Guest-specific fields */}
              {selectedUser.role === UserRole.GUEST && (
                <>
                  <Divider sx={{ my: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('users.guestInformation')}
                    </Typography>
                  </Divider>
                  
                  {selectedUser.phoneNumber && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('users.phoneNumber')}</Typography>
                      <Typography variant="body2">{selectedUser.phoneNumber}</Typography>
                    </Box>
                  )}
                  
                  {selectedUser.birthdate && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('users.birthDate')}</Typography>
                      <Typography variant="body2">{formatDate(selectedUser.birthdate)}</Typography>
                    </Box>
                  )}
                  
                  {selectedUser.nationalId && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('users.nationalId')}</Typography>
                      <Typography variant="body2">{selectedUser.nationalId}</Typography>
                    </Box>
                  )}
                  
                  {selectedUser.nationality && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('users.nationality')}</Typography>
                      <Typography variant="body2">{getCountryName(selectedUser.nationality)}</Typography>
                    </Box>
                  )}
                  
                  {selectedUser.sex && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('users.sex')}</Typography>
                      <Typography variant="body2">
                        {selectedUser.sex === 'M' ? t('users.male') : selectedUser.sex === 'F' ? t('users.female') : selectedUser.sex}
                      </Typography>
                    </Box>
                  )}
                  
                  {selectedUser.country && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('users.country')}</Typography>
                      <Typography variant="body2">{getCountryName(selectedUser.country)}</Typography>
                    </Box>
                  )}
                  
                  {selectedUser.city && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('users.city')}</Typography>
                      <Typography variant="body2">{selectedUser.city}</Typography>
                    </Box>
                  )}
                  
                  {selectedUser.postCode && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('users.postCode')}</Typography>
                      <Typography variant="body2">{selectedUser.postCode}</Typography>
                    </Box>
                  )}
                  
                  {selectedUser.passportId && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('users.passportId')}</Typography>
                      <Typography variant="body2">{selectedUser.passportId}</Typography>
                    </Box>
                  )}
                </>
              )}

              {(selectedUser.role === UserRole.ADMIN || selectedUser.role === UserRole.SUPER_ADMIN) && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {selectedUser.role === UserRole.SUPER_ADMIN ? t('sidebar.superAdmin') : t('sidebar.admin')} {t('users.protectedAccounts')}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>{t('users.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('users.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('users.deleteUserConfirm')} <strong>{selectedUser?.firstName} {selectedUser?.lastName}</strong>?
            {t('users.cannotBeUndone')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('login.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t('users.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onClose={() => setResetPasswordDialogOpen(false)}>
        <DialogTitle>{t('users.resetPasswordTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('users.resetPasswordFor')} <strong>{selectedUser?.firstName} {selectedUser?.lastName}</strong>
          </Typography>
          <TextField
            fullWidth
            type="password"
            label={t('users.newPassword')}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText={t('users.minimumChars')}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordDialogOpen(false)}>{t('login.cancel')}</Button>
          <Button
            onClick={handleResetPasswordConfirm}
            color="primary"
            variant="contained"
            disabled={newPassword.length < 6}
          >
            {t('users.resetPassword')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Room Management Dialog */}
      <Dialog 
        open={roomManagementDialogOpen} 
        onClose={() => setRoomManagementDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Manage Room Assignments</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Assign rooms to <strong>{selectedUser?.firstName} {selectedUser?.lastName}</strong>
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Select Rooms</InputLabel>
            <Select
              multiple
              value={selectedRooms}
              onChange={(e) => setSelectedRooms(e.target.value as string[])}
              label="Select Rooms"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((roomId) => {
                    const room = rooms.find(r => r.id === roomId);
                    return room ? <Chip key={roomId} label={`Room ${room.roomNumber}`} size="small" /> : null;
                  })}
                </Box>
              )}
            >
              {rooms.map((room) => (
                <MenuItem key={room.id} value={room.id}>
                  Room {room.roomNumber}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Alert severity="info" sx={{ mt: 2 }}>
            At least one room must be assigned to an operator.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoomManagementDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveRoomAssignments}
            color="primary"
            variant="contained"
            disabled={selectedRooms.length === 0}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UsersPage;

